import { cardsHelper } from './CardReducer';
import BaseCardBehavior from './CardBehavior/BaseCardBehavior';
import { CardTypeMap } from '../../Prototype/Level/Card/CardTypeMap';
import { CardTask } from './CardTasks';
import { Random } from '../../Utility/Random';
import { AnyBoosterType } from './BoosterType';
import { BoosterTypeMap } from './BoosterTypeMap';
import BaseBoosterBehavior, { IBaseBoosterBehaviorPlayMeta } from './BoosterBehavior/BaseBoosterBehavior';
import { CardType } from '../../Prototype/Level/Card/CardType';
import { DEFAULT_CARD_HEIGHT, DEFAULT_CARD_WIDTH } from '../../Prototype/Level/Card/ICardData';
import { CARD_OFFSET_IN_STACK, CARD_STACK_OFFSET, MAX_OPENED_CARDS_IN_STACK } from '../../Constants/InGame';
import { CardModifierType } from '../../Prototype/Level/Card/CardModifierType';
import CardStateModifierHelper from './CardStateModifierHelper';
import { ArrayHelper } from '../../Utility/ArrayHelper';
import { track, TrackMethod } from '../../Utility/Tracking/Track';
import { trackCardPlayed } from '../../Utility/Tracking/TrackingHelpers/Card';
import AudioStateHelper from '../Audio/AudioStateHelper';
import { IGameState } from '../Game/IGameState';
import {
    cardCastsTo,
    GoldCardData,
    ICardsState,
    ICardStateData,
    ICardStateTaskData,
    StreakReward,
    ValueCardData,
} from './ICardState';
import { JOKER_STREAK_REWARD_CHANCE, STREAK_PROGRESS_STEPS } from '../../Constants/Level';
import { cardColorBySuit, getCardSuit, Suit } from '../../Utility/CardsHelper';
import { ICardModifierData } from 'Prototype/Level/Card/ICardModifierData';
import { initialCardStateData } from './initialCardStateData';
import Game from 'Game';
import { PlayBoosterOptions } from './PlayBoosterOptions';
import { Logger, LogOrigin, LogStyles } from '../../Utility/Logger/LoggerSuite';
import { JOKER_PRE_BOOSTER_IN_STACK_PLACEMENT_RANGE } from '../../Constants/PreBoosters';
import { ILimit, MathHelper } from '../../Utility/MathHelper';
import { CardState } from 'Prototype/Level/Card/CardState';

export default class CardStateHelper {
    public static addCard(
        state: ICardsState,
        cardData: ICardStateData
    ): ICardsState {
        state.cards[cardData.id] = cardData;
        return state;
    }

    public static playBooster<T extends AnyBoosterType>(
        state: IGameState,
        type: T,
        options?: PlayBoosterOptions<T> extends IBaseBoosterBehaviorPlayMeta ? PlayBoosterOptions<T> : never
    ): IGameState {
        const behavior = CardStateHelper.getBoosterBehavior(
            state.levelState.cardsState,
            type
        );

        if (options?.force || behavior.isPlayable(state)) {
            state = behavior.play(state, options);

            // Handle card state after booster is played.
            let cardsState = CardStateHelper.updateCards(
                state.levelState.cardsState
            );

            // Check if cards need to be flipped.
            cardsState = CardStateHelper.checkCardsObscured(cardsState);

            // Check if next card should be folded.
            //cardsState = CardStateHelper.checkFoldNextCard(cardsState);

            // Update cards after a booster is played.
            state = {
                ...state,
                levelState: {
                    ...state.levelState,
                    cardsState: cardsState,
                },
            };
        }

        return state;
    }

    public static playCard(state: IGameState, cardID: string): IGameState {
        let cardState: ICardsState = state.levelState.cardsState;
        if (!cardState.cards[cardID]) return state;

        const behavior = CardStateHelper.getCardBehavior(cardState, cardID);
        if (behavior.isPlayable(cardState)) {
            const delayByCardID = behavior.getFlipDelays(cardState);
            state = trackCardPlayed(state);
            state = behavior.play(state);
            // Update card state to new card state
            cardState = state.levelState.cardsState;
            // testing audio delete it later
            AudioStateHelper.cardPlayed(cardState.cards[cardID]);

            // Increment the number of completed 'turns'.
            cardState = { ...cardState, turns: cardState.turns + 1 };

            // Check if cards need to be flipped.
            cardState = CardStateHelper.checkCardsObscured(cardState, delayByCardID);

            // Check if next card should be folded.
            //state = CardStateHelper.checkFoldNextCard(state);
        } else {
            if (
                CardStateHelper.getCardState(cardState, cardID) ===
                CardState.IN_PLAY
            ) {
                cardState.cards[cardID].attempts += 1;
                cardState = CardStateHelper.setStateTaskData(cardState, {
                    task: CardTask.WIGGLE,
                    cards: [cardID],
                });
            }
        }

        return {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState: cardState,
            },
        };
    }

    public static updateCard(
        state: ICardsState,
        cardID: string,
        cardData: Partial<ICardStateData>
    ): ICardsState {
        return {
            ...state,
            cards: {
                ...state.cards,
                [cardID]: {
                    ...state.cards[cardID],
                    ...cardData,
                } as ICardStateData
            }
        };
    }

    public static setCardInPlay(
        state: ICardsState,
        cardID: string
    ): ICardsState {
        CardStateHelper.removeFromStateArrays(state, cardID);
        state.inPlay.push(cardID);
        return state;
    }

    public static setCardInStack(
        state: ICardsState,
        cardID: string,
        options: {
            data?: Partial<ICardStateData>,
            index?: number
        } = {}
    ): ICardsState {
        const { data = {} } = options;
        CardStateHelper.removeFromStateArrays(state, cardID);
        state.cards[cardID] = {
            ...state.cards[cardID],
            faceUp: false,
            x: 0,
            y: 0,
            rotation: 0,
            ...data
        } as ICardStateData;

        if (options.index !== undefined) {
            state.inStack = ArrayHelper.insert(state.inStack, options.index, cardID);
        }
        else {
            state.inStack.push(cardID);
        }

        return state;
    }

    public static setCardInStackByIndex(
        state: ICardsState,
        cardID: string,
        indexToAdd: number
    ): ICardsState {
        CardStateHelper.removeFromStateArrays(state, cardID);
        state.cards[cardID] = {
            ...state.cards[cardID],
            rotation: 0,
        };
        state.inStack = ArrayHelper.insert(state.inStack,indexToAdd,cardID);
        return state;
    }

    public static setCardFolded(
        state: ICardsState,
        cardID: string
    ): ICardsState {
        CardStateHelper.removeFromStateArrays(state, cardID);
        state.cards[cardID] = {
            ...state.cards[cardID],
            faceUp: true,
            x: 0,
            y: 0,
            rotation: 0,
        };
        state.folded.push(cardID);
        return state;
    }

    public static setJokerCardFolded(
        state: ICardsState,
        cardID: string
    ): ICardsState {
        CardStateHelper.removeFromStateArrays(state, cardID);
        state.cards[cardID] = {
            ...state.cards[cardID],
            faceUp: true,
            rotation: 0,
        };
        state.folded.push(cardID);
        return state;
    }

    public static setCardDiscarded(
        state: ICardsState,
        ...cardIDs: string[]
    ): ICardsState {
        for (let i = 0; i < cardIDs.length; i++) {
            const cardID = cardIDs[i];
            CardStateHelper.removeFromStateArrays(state, cardID);
            state.discarded.push(cardID);
            state = CardStateModifierHelper.onCardDiscarded(state, cardID);
        }

        return state;
    }

    public static setJokerPreBoosterCardInStack(state: ICardsState, cardID: string): ICardsState {
        const range: ILimit = JOKER_PRE_BOOSTER_IN_STACK_PLACEMENT_RANGE;

        // +1 here because the joker card is not in the stack yet
        const stackCount: number = Math.min(MAX_OPENED_CARDS_IN_STACK, state.inStack.length + 1);
        const randomDistanceFromTop: number = Random.int(
            // Min Range: If stack count is < min, then use stack count as the min else use default min
            stackCount < range.min ? stackCount : range.min,
            // Max range: Take which ever is lower, stack count or max range
            Math.min(stackCount, range.max),
        );

        // -1 here to adjust for count -> index
        const index: number = MathHelper.clamp(stackCount - randomDistanceFromTop, 0, stackCount - 1);

        return CardStateHelper.setCardInStack(state, cardID, { index });
    }

    /**
     * TODO: Make immutable,
     * Current uses rely on mutating the state
     * */
    /**
     * Removes specified card ID from all arrays - overwrites all arrays.
     * @param state
     * @param cardID
     */
    public static removeFromStateArrays(
        state: ICardsState,
        cardID: string
    ): ICardsState {
        state.inPlay = state.inPlay.filter(value => value !== cardID);
        state.inStack = state.inStack.filter(value => value !== cardID);
        state.folded = state.folded.filter(value => value !== cardID);
        state.discarded = state.discarded.filter(value => value !== cardID);

        return state;
    }

    public static checkCardsObscured(
        state: ICardsState,
        delayByCardID: Record<string, number> | null = null,
    ): ICardsState {
        const flippedCards: string[] = [];

        state.inPlay.forEach(cardID => {
            const behavior = CardStateHelper.getCardBehavior(state, cardID);

            // Set faceUp to true if the card was previously face-down and is no longer obscured.
            if (
                state.cards[cardID].faceUp === false &&
                !behavior.isObscured(state)
            ) {
                state.cards[cardID] = {
                    ...state.cards[cardID],
                    faceUp: true,
                };

                // Collect cards for flipping animations.
                flippedCards.push(cardID);
            }
        });

        if (flippedCards.length > 0) {
            state = CardStateHelper.setStateTaskData(state, {
                task: CardTask.FLIP,
                cards: flippedCards,
                delayByCardID,
            });
        }

        return state;
    }

    public static checkFoldNextCard(state: ICardsState): ICardsState {
        if (state.inStack.length > 0 && state.folded.length === 0) {
            const cardID = state.inStack[state.inStack.length - 1];
            state = CardStateHelper.setCardFolded(state, cardID);
            state = CardStateHelper.setStateTaskData(state, {
                task: CardTask.FOLD,
                cards: [cardID],
            });
        }

        return state;
    }

    public static getCardState(state: ICardsState, cardID: string): CardState {
        if (state.inPlay.indexOf(cardID) !== -1) {
            return CardState.IN_PLAY;
        }
        if (state.inStack.indexOf(cardID) !== -1) {
            return CardState.IN_STACK;
        }
        if (state.folded.indexOf(cardID) !== -1) {
            return CardState.FOLDED;
        }
        if (state.discarded.indexOf(cardID) !== -1) {
            return CardState.DISCARDED;
        }
        return CardState.UNKNOWN;
    }

    public static getCardBehavior(
        state: ICardsState,
        cardID: string
    ): BaseCardBehavior {
        return new CardTypeMap[state.cards[cardID].type].behavior(cardID);
    }

    public static getBoosterBehavior(
        state: ICardsState,
        type: AnyBoosterType,
    ): BaseBoosterBehavior {
        return new BoosterTypeMap[type].behavior();
    }

    public static getCurrentCard(state: ICardsState): string {
        return state.folded[state.folded.length - 1];
    }

    /**
     * Update all cards (called after a 'valid' turn).
     * @param state
     */
    public static updateCards(state: ICardsState): ICardsState {
        Object.keys(state.cards).forEach(
            cardID =>
                (state = CardStateHelper.getCardBehavior(state, cardID).update(
                    state
                ))
        );

        return state;
    }

    public static setStateTaskData(
        state: ICardsState,
        taskData: ICardStateTaskData
    ): ICardsState {
        return {
            ...state,
            tasks: [...state.tasks, taskData],
        };
    }

    public static getCardMatches(state: ICardsState): string[] {
        const playableCards: string[] = state.inPlay.filter(inPlayCardID => {
            const behavior = CardStateHelper.getCardBehavior(
                state,
                inPlayCardID
            );
            const isPlayable: boolean = behavior.isPlayable(state);
            return isPlayable;
        });
        return playableCards;
    }

    public static plusStreakCards(state: IGameState): ICardsState {
        let cardsState = state.levelState.cardsState;
        if (!cardsState.streakEnabled) {
            return cardsState;
        }

        const lastCardColor = cardsState.matchSequence[cardsState.matchSequence.length - 1];
        const multiplier = cardsState.matchSequence.slice(-5).every((c) => c === lastCardColor) ? 2 : 1;
        const targets: string[] = [];

        // if was all same suit, track it
        if(multiplier === 2) {
            track('mStreakColorNum', 1, TrackMethod.ADD);
        }
        track('mStreakNum', 1, TrackMethod.ADD);

        if (cardsState.streakReward === StreakReward.Joker) {
            track('mJokerCardsWon', multiplier, TrackMethod.ADD);

            for (let i = 0; i < multiplier; i++) {
                const newCardID = `card_joker_${Random.ID()}`;
                targets.push(newCardID);

                cardsState = CardStateHelper.addCard(cardsState, {
                    ...initialCardStateData,
                    id: newCardID,
                    type: CardType.joker_pre_booster,
                    faceUp: true,
                    madeByBooster: false,
                });

                cardsState = CardStateHelper.setJokerPreBoosterCardInStack(cardsState, newCardID);
            }

            cardsState = CardStateHelper.setStateTaskData(cardsState, {
                task: CardTask.PLUS_STREAK_JOKER,
                cards: targets,
            });
        } else {
            const newCardCount: number = (cardsState.streakReward === StreakReward.OneCard ? 1 : 2) * multiplier;

            track('mExtraCardsWon', newCardCount, TrackMethod.ADD);

            cardsState = this.addNewCards(cardsState, newCardCount, targets);

            // Trigger plus streak cards animation task.
            cardsState = CardStateHelper.setStateTaskData(cardsState, {
                task: CardTask.PLUS_STREAK,
                cards: targets,
            });
        }

        const canBeJoker = state.levelState.levelNumber > 6 && Math.random() <= JOKER_STREAK_REWARD_CHANCE;

        const nextReward = canBeJoker ? StreakReward.Joker : StreakReward.TwoCards;
        cardsState = CardStateHelper.setStreakReward(cardsState, nextReward);

        return cardsState;
    }

    public static addNewCards(
        state: ICardsState,
        amount: number,
        cardIDs: string[]
    ): ICardsState {
        // Keep track of card values drawn to prevent duplicate values.
        const drawnValues: number[] = [];
        for (let i = 0; i < amount; i++) {
            const newCardID = `card_${Random.ID()}`;

            // Draw a new card.
            let value = cardsHelper.draw();

            // If the un-suited value of the card was already drawn keep trying.
            while (drawnValues.indexOf(value % 13) !== -1) {
                value = cardsHelper.draw();
            }

            // Store the un-suited value of the card.
            drawnValues.push(value % 13);

            state = CardStateHelper.addCard(state, {
                ...initialCardStateData,
                id: newCardID,
                type: CardType.value,
                faceUp: false,
                depth: 0,
                rotation: 0,
                modifiers: [],
                sequence: 0,
                attempts: 0,
                value: value,
                random: true,
                x: 0,
                y: 0,
                width: DEFAULT_CARD_WIDTH,
                height: DEFAULT_CARD_HEIGHT,
            });
            state = CardStateHelper.setCardInStack(state, newCardID);
            cardIDs.push(newCardID);
        }

        return state;
    }

    public static increaseStreak(
        state: ICardsState,
        cardScore: number,
        cardID: string,
    ): ICardsState {
        const card = state.cards[cardID];

        if (!cardCastsTo(card, CardType.value) && !cardCastsTo(card, CardType.gold)) {
            return state;
        }

        track('mHighestPlayStreak', state.matchSequence.length + 1, TrackMethod.MAX);

        return {
            ...state,
            matchSequence: [...state.matchSequence, cardColorBySuit[CardStateHelper.getCardSuit(card)]],
            streakScore: state.streakScore + cardScore,
        };
    }

    public static getCardSuit(card: ValueCardData | GoldCardData): Suit {
        return cardCastsTo(card, CardType.gold) ? card.suit : getCardSuit(card.value);
    }

    public static increaseStreakScore(state: ICardsState, cardScore: number): ICardsState {
        return {
            ...state,
            streakScore: state.streakScore + cardScore,
        };
    }

    public static cancelMatchSequence(state: ICardsState): ICardsState {
        if (state.turns === 0) {
            return state;
        }

        return {
            ...state,
            matchSequence: [],
            streakScore: 0,
        };
    }

    public static cancelStreakScore(state: ICardsState): ICardsState {
        return {
            ...state,
            streakScore: 0,
        };
    }

    public static addTurnScore(
        state: ICardsState,
        ...score: number[]
    ): ICardsState {
        return {
            ...state,
            turnScore: state.turnScore + score.reduce((a, c) => a + c, 0),
        };
    }

    public static pushUndoHistory(
        state: ICardsState,
        cardID: string,
        cardState: CardState
    ): ICardsState {
        // Ignore first turn.
        if (state.turns === 0) return state;

        return {
            ...state,
            undoHistory: [
                ...state.undoHistory,
                {
                    ...state.cards[cardID],
                    cards: {...state.cards},
                    modifiers: [],
                    cardState: cardState,
                    matchSequence: state.matchSequence,
                    streakScore: state.streakScore,
                },
            ],
        };
    }

    public static clearUndoHistory(state: ICardsState): ICardsState {
        return {
            ...state,
            undoHistory: [],
        };
    }

    public static checkStreakBonus(state: IGameState): ICardsState {
        let cardsState = state.levelState.cardsState;
        // 1st bonus streak rewards + 1 card.
        // 2nd, 3rd ... bonus streaks reward + 2 cards.
        if (cardsState.matchSequence.length > 0 && cardsState.matchSequence.length % STREAK_PROGRESS_STEPS === 0) {
            cardsState = CardStateHelper.plusStreakCards(state);
        }
        return cardsState;
    }

    public static setStreakReward(state: ICardsState, reward: StreakReward): ICardsState {
        return {
            ...state,
            streakReward: reward,
        };
    }

    /**
     * Face-up and face-down cards are shuffled separately and concatenated.
     * This is done to prioritize replacing face-up cards with face-down cards when shuffling all value cards.
     * @param state
     */
    public static shuffleValueCards(state: ICardsState): ICardsState {
        const faceUpCardIDs: string[] = [];
        const faceDownCardIDs: string[] = [];

        CardStateHelper.getValueCards(state).forEach(cardID => {
            const behavior = CardStateHelper.getCardBehavior(state, cardID);
            if (behavior.isBlockedByModifier(state, CardModifierType.connected)) {
                return;
            }

            if (behavior.isFaceUp(state)) {
                faceUpCardIDs.push(cardID);
            } else {
                faceDownCardIDs.push(cardID);
            }
        });

        // Note the order of the following shuffled arrays: face-up and face-down are reversed.
        const shuffledCardIDs = [
            ...ArrayHelper.shuffle(faceUpCardIDs, true),
            ...ArrayHelper.shuffle(faceDownCardIDs, true),
        ];
        const shuffledTargetCardIDs = [
            ...ArrayHelper.shuffle(faceDownCardIDs, true),
            ...ArrayHelper.shuffle(faceUpCardIDs, true),
        ];

        const shuffledCards = { ...state.cards };

        shuffledCardIDs.forEach((cardID: string, index: number) => {
            shuffledCards[cardID] = {
                ...shuffledCards[cardID],
                value: (state.cards[shuffledTargetCardIDs[index]] as ValueCardData).value,
            } as ValueCardData;
        });

        return {
            ...state,
            cards: shuffledCards,
        };
    }

    /**
     * Get all value cards in-play unless blocked by fog.
     * @param state
     */
    public static getValueCards(state: ICardsState): string[] {
        return state.inPlay.filter(cardID => {
            if (state.cards[cardID].type !== CardType.value) return false;

            const modifier = state.cards[cardID].modifiers.find(
                modifierData => modifierData.type === CardModifierType.fog
            );
            if (modifier) {
                const behavior = CardStateModifierHelper.getModifierBehavior(
                    state,
                    modifier,
                    cardID
                );
                return !behavior.isBlocking(state);
            }

            return true;
        });
    }

    public static removeCard(
        state: ICardsState,
        cardID: string
    ): ICardsState {
        CardStateHelper.removeFromStateArrays(state, cardID);

        const cards = { ...state.cards };
        delete cards[cardID];
        state = { ...state, cards };

        return state;
    }

    public static addModifierToCard(
        state: ICardsState,
        cardID: string,
        modifierData: ICardModifierData
    ): ICardsState {
        const card = state.cards[cardID];

        if (card.modifiers.some((data) => data.type === modifierData.type)) {
            Logger.log(
                `Modifier of type '${modifierData.type}' already exists on card.`,
                LogOrigin.CARD_STATE_HELPER,
                LogStyles.WARNING
            );
            return state;
        }

        return {
            ...state,
            cards: {
                ...state.cards,
                [cardID]: {
                    ...card,
                    modifiers: [...card.modifiers, modifierData]
                }
            }
        }
    }
    public static foldStackCard(
        state: IGameState,
        cardID: string,
        options: {
            queueNext?: boolean
            skipAnimation?: boolean
        } = {}
    ): IGameState {
        let cardsState = CardStateHelper.getCardsState(state);
        cardsState = CardStateHelper.pushUndoHistory(
            cardsState,
            cardID,
            CardState.IN_STACK
        );

        const alreadyFolded: boolean = cardsState.folded.includes(
            cardID
        );
        cardsState = CardStateHelper.setCardFolded(
            cardsState,
            cardID
        );

        if (!options.skipAnimation) {
            cardsState = CardStateHelper.setStateTaskData(cardsState, {
                task: CardTask.FOLD,
                cards: [cardID],
                alreadyFolded,
                queueNext: options.queueNext
            });
        }

        state = CardStateHelper.updateGameState(state, cardsState);

        state = CardStateModifierHelper.updateModifiers(
            state,
            cardID,
            true
        );

        cardsState = CardStateHelper.getCardsState(state);
        cardsState = CardStateHelper.cancelMatchSequence(cardsState);

        return CardStateHelper.updateGameState(state, cardsState);
    }

    /**
     * Get all cards inPlay excluding the types selected
     * @param state
     * @param cardType
     */
    public static getCardsInPlayMinus(
        state: ICardsState,
        cardType: CardType[]
    ): string[] {
        const cardsToKeep: string[] = [];
        state.inPlay.forEach(cardID => {
            if (!cardType.includes(state.cards[cardID].type)) {
                cardsToKeep.push(cardID);
            }
        });

        return cardsToKeep;
    }

    public static updateGameState(state: IGameState, cardsState: ICardsState) {
        return {...state,
            levelState: {
                ...state.levelState,
                cardsState
            }
        };
    }

    public static getCardsState(state: IGameState): ICardsState {
        return { ...state.levelState.cardsState };
    }

    public static getModifiersOfType(cardsState: ICardsState, cardID: string, ...modifierTypes: CardModifierType[]): ICardModifierData[] {
        return cardsState.cards[cardID].modifiers.filter(modifiers => modifierTypes.includes(modifiers.type));
    }
}

export const levelContainsCardOfType = (
    cardsState: ICardsState,
    cardType: CardType
): boolean => {
    return Object.values(cardsState.cards).some(
        card => card?.type === cardType
    );
};

export const levelContainsCardWithModifier = (
    cardsState: ICardsState,
    modifierType: CardModifierType
): boolean => {
    cardsState;
    return Object.values(cardsState.cards).some(card =>
        card?.modifiers.some(modifiers => modifiers.type === modifierType)
    );
};

export const hasModifierOfType = (
    cardsState: ICardsState,
    cardID: string,
    modifierType: CardModifierType
): boolean => {
    return cardsState.cards[cardID].modifiers.some(modifiers => modifiers.type === modifierType);
};

export const getStackCardTargetX = (
    index: number,
    stackLength: number = Game.store.getState().levelState.cardsState.inStack.length,
) => {
    const reversedIndex = stackLength - 1 - index;
    return CARD_STACK_OFFSET + Math.min(MAX_OPENED_CARDS_IN_STACK - 1, reversedIndex) * CARD_OFFSET_IN_STACK;
}

export const returnFoldedToStack = (cardsState: ICardsState): ICardsState => {
    if (cardsState.folded.length = 0) return cardsState;
    const currentCard = CardStateHelper.getCurrentCard(cardsState);

    if (!currentCard) return cardsState;

    cardsState = CardStateHelper.setCardInStack(
        cardsState,
        currentCard
    );

    // Task for animation.
    return CardStateHelper.setStateTaskData(cardsState, {
        task: CardTask.UNDO_FOLD,
        cards: [currentCard],
    });
}

export const isCurrentCardJoker = (cardsState: ICardsState): boolean => {
    if (cardsState.folded.length > 0) {
        const currentCardID: string = CardStateHelper.getCurrentCard(cardsState);
        const currentCardData = cardsState.cards[currentCardID];
        return isCardTypeJoker(currentCardData.type);
    }
    return false;
}

export const isCardTypeJoker = (type: CardType): boolean => {
    return (type === CardType.joker || type === CardType.joker_pre_booster);
}
